import {
  Box,
  Typography,
  TextField,
  Container,
  Button,
  InputBase,
  IconButton,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  Chip,
  Autocomplete,
  styled,
  Switch,
  SwitchProps,
  Paper,
  Stack,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import { useGetCompanyMembersQuery } from "@/features/Members/api";
import { ContentCopy } from "@mui/icons-material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { toast } from "react-toastify";
import AdvanceTab from "./AdvanceTab";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import {
  useGetListOfStatusQuery,
  useUpdateProjectMutation,
} from "@/features/bughurd/api";
import moment from "moment";
import CustomColorPicker from "./CustomColorPicker";
import DEFEKT_TRACKER from "@/app/permissionNew/defectTracker";
import { useAppSelector } from "@/app/hooks";
import PersonIcon from "@mui/icons-material/Person";
import PaletteIcon from "@mui/icons-material/Palette";
import ViewKanbanIcon from "@mui/icons-material/ViewKanban";
import CategoryIcon from "@mui/icons-material/Category";
import WorkspacesIcon from "@mui/icons-material/Workspaces";
import TimerIcon from "@mui/icons-material/Timer";
import LinkIcon from "@mui/icons-material/Link";
import BugReportIcon from "@mui/icons-material/BugReport";
import FlagIcon from "@mui/icons-material/Flag";
import KeyIcon from "@mui/icons-material/Key";
import UpdateIcon from "@mui/icons-material/Update";
import LanguageIcon from "@mui/icons-material/Language";
import LabelIcon from "@mui/icons-material/Label";
import SettingsIcon from "@mui/icons-material/Settings";
import BusinessIcon from "@mui/icons-material/Business";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";

const skillSetOptions = [
  "Frontend Development",
  "Backend Development",
  "Full Stack Development",
  "DevOps",
  "QA/Testing",
  "UI/UX Design",
  "Mobile Development",
  "Data Science",
  "Machine Learning",
  "Security",
];

const IOSSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 24,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(18px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: theme.palette.primary.main,
        opacity: 1,
        border: 0,
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 20,
    height: 20,
  },
  "& .MuiSwitch-track": {
    borderRadius: 12,
    backgroundColor: theme.palette.mode === "light" ? "#E9E9EA" : "#39393D",
    opacity: 1,
  },
}));

const SectionHeader = ({ icon, title }) => (
  <Box
    sx={{
      px: 2,
      py: 1.5,
      borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
      background: (theme) =>
        theme.palette.mode === "dark"
          ? "rgba(255, 255, 255, 0.02)"
          : "rgba(0, 0, 0, 0.015)",
    }}
  >
    <Box display="flex" alignItems="center" gap={1.2}>
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: "6px",
          background: (theme) => `${theme.palette.primary.main}15`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </Box>
      <Typography
        variant="subtitle2"
        fontWeight={600}
        sx={{
          fontSize: "13px",
          color: (theme) => theme.palette.text.primary,
        }}
      >
        {title}
      </Typography>
    </Box>
  </Box>
);

const SettingRow = ({
  icon,
  label,
  subtitle = null,
  children,
  noBorder = false,
}) => (
  <Box
    sx={{
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      px: 2,
      py: 1.5,
      gap: 1.5,
      borderBottom: (theme) =>
        noBorder ? "none" : `1px solid ${theme.palette.divider}`,
      minHeight: "52px",
      transition: "background 0.15s ease",
      "&:hover": {
        background: (theme) =>
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.02)"
            : "rgba(0, 0, 0, 0.01)",
      },
    }}
  >
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: "6px",
        background: (theme) =>
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.04)"
            : "rgba(0, 0, 0, 0.03)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box
      sx={{
        minWidth: subtitle ? "140px" : "100px",
        flex: subtitle ? "1 1 180px" : "0 1 120px",
        maxWidth: subtitle
          ? { xs: "100%", sm: "300px" }
          : { xs: "100%", sm: "150px" },
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: (theme) => theme.palette.text.primary,
          fontSize: "13px",
          fontWeight: 500,
          lineHeight: 1.4,
          wordBreak: "break-word",
        }}
      >
        {label}
      </Typography>
      {subtitle && (
        <Typography
          variant="caption"
          sx={{
            color: (theme) => theme.palette.text.secondary,
            fontSize: "11px",
            lineHeight: 1.4,
            display: "block",
            mt: 0.3,
            wordBreak: "break-word",
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        flex: "1 1 auto",
        justifyContent: "flex-end",
        minWidth: "180px",
        ml: "auto",
      }}
    >
      {children}
    </Box>
  </Box>
);

// Common styles for form controls
const formControlStyles = {
  select: {
    fontSize: "12.5px",
    height: "34px",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: (theme) =>
        theme.palette.mode === "dark"
          ? "rgba(255, 255, 255, 0.12)"
          : "rgba(0, 0, 0, 0.12)",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: (theme) =>
        theme.palette.mode === "dark"
          ? "rgba(255, 255, 255, 0.2)"
          : "rgba(0, 0, 0, 0.23)",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: (theme) => theme.palette.primary.main,
    },
  },
  autocomplete: {
    "& .MuiOutlinedInput-root": {
      fontSize: "12.5px",
      minHeight: "34px",
      padding: "3px 9px",
      "& fieldset": {
        borderColor: (theme) =>
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.12)"
            : "rgba(0, 0, 0, 0.12)",
      },
      "&:hover fieldset": {
        borderColor: (theme) =>
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.2)"
            : "rgba(0, 0, 0, 0.23)",
      },
      "&.Mui-focused fieldset": {
        borderColor: (theme) => theme.palette.primary.main,
      },
    },
  },
  chip: {
    fontSize: "11px",
    height: "24px",
    fontWeight: 500,
  },
};

const SettingsTab = ({ projectDetails, onProjectUpdate }) => {
  const { roleCodeDetails: myPermissions, currentUser } = useAppSelector(
    (state) => state.appGlobal,
  );
  const isSupport = projectDetails?.configurationType === "SUPPORT" || false;
  const { data: companyMemberData } = useGetCompanyMembersQuery({});
  const { t } = useTranslation(["bugherd"]);
  const [isEditMode, setIsEditMode] = useState(false);

  const [projectName, setProjectName] = useState(projectDetails?.name || "");
  const [projectDomain, setProjectDomain] = useState(
    projectDetails?.domain || "",
  );
  const [projectColor, setProjectColor] = useState(
    projectDetails?.icon || "NA",
  );
  const [projectBoardType, setProjectBoardType] = useState(
    projectDetails?.boardType || "KANBAN",
  );
  const [projectType, setProjectType] = useState(
    projectDetails?.projectType || "",
  );
  const [itemTypes, setItemTypes] = useState(projectDetails?.itemTypes || []);
  const [skillSet, setSkillSet] = useState(projectDetails?.skillSet || []);
  const [doneStatusId, setDoneStatusId] = useState(
    projectDetails?.doneStatus?._id || projectDetails?.doneStatus || "",
  );
  const [isEstimationRequired, setIsEstimationRequired] = useState(
    projectDetails?.isEstimationRequired || false,
  );
  const [storyRequiredTypes, setStoryRequiredTypes] = useState<string[]>(
    projectDetails?.storyRequiredTypes?.length > 0
      ? projectDetails.storyRequiredTypes
      : ["DEFECT", "SUGGESTIONS"],
  );
  const [preference, setPreference] = useState(
    projectDetails?.preference || {
      isFeedback: true,
      isBoard: true,
      isLiveLog: true,
    },
  );
  const [isAutoTaskFromSystemBug, setIsAutoTaskFromSystemBug] = useState(
    projectDetails?.isAutoTaskFromSystemBug || false,
  );
  const [systemAutoBugStatusId, setSystemAutoBugStatusId] = useState(
    projectDetails?.autoBugStatus?._id || projectDetails?.autoBugStatus || "",
  );
  const projectKey = projectDetails?.projectKey || "";
  const owner =
    companyMemberData?.data?.decryptedData?.find(
      (owner) => owner?._id === projectDetails?.owner,
    )?.fullName || "";

  const { data: statusLists, isLoading: isStatusLoading } =
    useGetListOfStatusQuery(
      { projectId: projectDetails?._id },
      { skip: !projectDetails?._id },
    );
  const availableStatus = statusLists?.data?.decryptedData?.items || [];

  const baseUrl = window?.location?.origin;
  const isSniffer = baseUrl?.includes("sniffer") || false;


  useEffect(() => {
    if (isEditMode) return;
    setProjectName(projectDetails?.name || "");
    setProjectDomain(projectDetails?.domain || "");
    setProjectColor(projectDetails?.icon || "NA");
    setProjectBoardType(projectDetails?.boardType || "KANBAN");
    setProjectType(projectDetails?.projectType || "");
    setItemTypes(projectDetails?.itemTypes || []);
    setSkillSet(projectDetails?.skillSet || []);
    setIsEstimationRequired(projectDetails?.isEstimationRequired || false);
    setStoryRequiredTypes(
      projectDetails?.storyRequiredTypes?.length > 0
        ? projectDetails.storyRequiredTypes
        : ["DEFECT", "SUGGESTIONS"],
    );
    setPreference(
      projectDetails?.preference || {
        isFeedback: true,
        isBoard: true,
        isLiveLog: true,
      },
    );
    setIsAutoTaskFromSystemBug(
      projectDetails?.isAutoTaskFromSystemBug || false,
    );
    setSystemAutoBugStatusId(
      projectDetails?.autoBugStatus?._id || projectDetails?.autoBugStatus || "",
    );
    setDoneStatusId(
      projectDetails?.doneStatus?._id || projectDetails?.doneStatus || "",
    );
  }, [projectDetails]);

  const [updateProject, { isLoading: isSaving }] = useUpdateProjectMutation();

  const handleCopyProjectKey = () => {
    toast.success(t("Project Key copied to clipboard"));
    navigator.clipboard.writeText(projectKey);
  };

  const handleSaveAll = async () => {
    try {
      const payload = {
        projectId: projectDetails?._id,
        name: projectName,
        domain: projectDomain,
        platform: projectDetails?.platform,
        owner: projectDetails?.owner,
        icon: projectColor,
        boardType: projectBoardType,
        projectType: projectDetails?.projectType,
        itemTypes: itemTypes.map((item) => ({
          ...item,
          label: item.label.toUpperCase(),
        })),
        skillSet,
        isEstimationRequired,
        storyRequiredTypes,
        preference,
        isAutoTaskFromSystemBug,
        autoBugStatus: isAutoTaskFromSystemBug
          ? systemAutoBugStatusId || null
          : null,
        doneStatus: doneStatusId || null,
      };
      const res = await updateProject(payload);
      if (res?.data) {
        toast.success(t("Project settings updated successfully"));
        setIsEditMode(false);
        onProjectUpdate();
      } else {
        toast.error(
          (res as any)?.error?.data?.message ||
            t("Failed to update project settings"),
        );
      }
    } catch {
      toast.error(t("Failed to update project settings"));
    }
  };

  const handleCancel = () => {
    setProjectName(projectDetails?.name || "");
    setProjectDomain(projectDetails?.domain || "");
    setProjectColor(projectDetails?.icon || "NA");
    setProjectBoardType(projectDetails?.boardType || "KANBAN");
    setProjectType(projectDetails?.projectType || "");
    setItemTypes(projectDetails?.itemTypes || []);
    setSkillSet(projectDetails?.skillSet || []);
    setDoneStatusId(
      projectDetails?.doneStatus?._id || projectDetails?.doneStatus || "",
    );
    setIsEstimationRequired(projectDetails?.isEstimationRequired || false);
    setStoryRequiredTypes(
      projectDetails?.storyRequiredTypes?.length > 0
        ? projectDetails.storyRequiredTypes
        : ["DEFECT", "SUGGESTIONS"],
    );
    setPreference(
      projectDetails?.preference || {
        isFeedback: true,
        isBoard: true,
        isLiveLog: true,
      },
    );
    setIsAutoTaskFromSystemBug(
      projectDetails?.isAutoTaskFromSystemBug || false,
    );
    setSystemAutoBugStatusId(
      projectDetails?.autoBugStatus?._id || projectDetails?.autoBugStatus || "",
    );
    setIsEditMode(false);
  };

  useEffect(() => {
    const hasSavedDoneStatus =
      projectDetails?.doneStatus?._id || projectDetails?.doneStatus;
    if (!hasSavedDoneStatus && !doneStatusId && availableStatus.length > 0) {
      const doneMatch = availableStatus.find((s) => {
        const name = (s?.status || s?.label || s?.name || "").toLowerCase();
        return name === "done" || name === "completed";
      });
      const target = doneMatch || availableStatus[availableStatus.length - 1];
      if (target?._id) {
        setDoneStatusId(target._id);
      }
    }
  }, [availableStatus, doneStatusId, projectDetails]);

  const capitalizeLabel = (label) => {
    return label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
  };

  const doneStatusObj = availableStatus.find((s) => s._id === doneStatusId);
  const doneStatusName =
    doneStatusObj?.status ||
    doneStatusObj?.label ||
    doneStatusObj?.name ||
    t("Not Selected");

  const autoBugStatusObj = availableStatus.find(
    (s) => s._id === systemAutoBugStatusId,
  );
  const autoBugStatusName =
    autoBugStatusObj?.status ||
    autoBugStatusObj?.label ||
    autoBugStatusObj?.name ||
    t("Not Selected");

  return (
    <Container
      maxWidth={false}
      sx={{
        maxWidth: "1200px",
        margin: "0 auto",
        p: 0,
        pb: 3,
        maxHeight: "calc(100vh - 140px)",
        overflowY: "auto",
        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-track": {
          background: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
          background: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.2)"
              : "rgba(0, 0, 0, 0.2)",
          borderRadius: "3px",
        },
      }}
    >
      {/* Page Header */}
      <Box mb={2.5}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={1.5}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <SettingsIcon
              sx={{
                fontSize: 24,
                color: (theme) => theme.palette.primary.main,
              }}
            />
            <Typography
              variant="h5"
              fontWeight={600}
              sx={{
                color: (theme) => theme.palette.text.primary,
                fontSize: "20px",
              }}
            >
              {t("Project Settings")}
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            {!isEditMode ? (
              !DEFEKT_TRACKER.HIDE_RENAME(myPermissions) &&
              DEFEKT_TRACKER.RENAME(myPermissions) && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => setIsEditMode(true)}
                >
                  {t("Edit")}
                </Button>
              )
            ) : (
              <>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={
                    isSaving ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  onClick={handleSaveAll}
                  disabled={isSaving}
                >
                  {t("Save")}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  {t("Cancel")}
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Box>

      <Grid container spacing={2}>
        {/* LEFT COLUMN */}
        <Grid item xs={12} md={6}>
          <Stack spacing={2}>
            {/* GENERAL INFORMATION */}
            <Paper
              elevation={0}
              sx={{
                background: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.03)"
                    : theme.palette.background.paper,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              <SectionHeader
                icon={
                  <BusinessIcon
                    sx={{
                      fontSize: 16,
                      color: (theme) => theme.palette.primary.main,
                    }}
                  />
                }
                title={t("General Information")}
              />
              <Box>
                <SettingRow
                  icon={
                    <LabelIcon
                      sx={{
                        fontSize: 16,
                        color: (theme) => theme.palette.text.secondary,
                      }}
                    />
                  }
                  label={t("Name")}
                >
                  {isEditMode ? (
                    <InputBase
                      value={projectName}
                      onChange={(e) => setProjectName(e.target.value)}
                      autoFocus
                      sx={{
                        minWidth: 180,
                        px: 1.5,
                        py: 0.5,
                        border: (theme) =>
                          `1px solid ${theme.palette.primary.main}`,
                        borderRadius: "6px",
                        fontSize: "12.5px",
                      }}
                    />
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "12.5px",
                        fontWeight: 500,
                        color: (theme) => theme.palette.text.primary,
                      }}
                    >
                      {projectName}
                    </Typography>
                  )}
                </SettingRow>

                <SettingRow
                  icon={
                    <LanguageIcon
                      sx={{
                        fontSize: 16,
                        color: (theme) => theme.palette.text.secondary,
                      }}
                    />
                  }
                  label={t("Domain")}
                >
                  {isEditMode ? (
                    <InputBase
                      value={projectDomain}
                      onChange={(e) => setProjectDomain(e.target.value)}
                      placeholder="example.com"
                      sx={{
                        minWidth: 180,
                        px: 1.5,
                        py: 0.5,
                        border: (theme) =>
                          `1px solid ${theme.palette.primary.main}`,
                        borderRadius: "6px",
                        fontSize: "12.5px",
                      }}
                    />
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "12.5px",
                        fontWeight: 500,
                        color: (theme) => theme.palette.text.primary,
                      }}
                    >
                      {projectDomain || t("Not Set")}
                    </Typography>
                  )}
                </SettingRow>

                <SettingRow
                  icon={
                    <PersonIcon
                      sx={{
                        fontSize: 16,
                        color: (theme) => theme.palette.text.secondary,
                      }}
                    />
                  }
                  label={t("Owner")}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "12.5px",
                      fontWeight: 500,
                      color: (theme) => theme.palette.text.primary,
                    }}
                  >
                    {owner || t("Not Assigned")}
                  </Typography>
                </SettingRow>

                <SettingRow
                  icon={
                    <PaletteIcon
                      sx={{
                        fontSize: 16,
                        color: (theme) => theme.palette.text.secondary,
                      }}
                    />
                  }
                  label={t("Color")}
                >
                  <CustomColorPicker
                    value={projectColor}
                    onChange={
                      isEditMode ? (color) => setProjectColor(color) : undefined
                    }
                    size="small"
                    variant="outlined"
                    showHexValue={false}
                    disabled={!isEditMode}
                  />
                </SettingRow>

                <SettingRow
                  icon={
                    <KeyIcon
                      sx={{
                        fontSize: 16,
                        color: (theme) => theme.palette.text.secondary,
                      }}
                    />
                  }
                  label={t("Key")}
                  noBorder
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      background: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.04)"
                          : "rgba(0, 0, 0, 0.03)",
                      borderRadius: "6px",
                      px: 1.25,
                      py: 0.5,
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "11.5px",
                        fontWeight: 600,
                        color: (theme) => theme.palette.primary.main,
                      }}
                    >
                      {projectKey}
                    </Typography>
                    <IconButton onClick={handleCopyProjectKey} size="small">
                      <ContentCopy sx={{ fontSize: 12 }} />
                    </IconButton>
                  </Box>
                </SettingRow>
              </Box>
            </Paper>

            {/* METADATA */}
            {/* <Paper
              elevation={0}
              sx={{
                background: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.03)"
                    : theme.palette.background.paper,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              <SectionHeader
                icon={
                  <UpdateIcon
                    sx={{
                      fontSize: 16,
                      color: (theme) => theme.palette.primary.main,
                    }}
                  />
                }
                title={t("Metadata")}
              />
              <Box>
                <SettingRow
                  icon={
                    <UpdateIcon
                      sx={{
                        fontSize: 16,
                        color: (theme) => theme.palette.text.secondary,
                      }}
                    />
                  }
                  label={t("Last Updated")}
                  noBorder
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "12.5px",
                      fontWeight: 500,
                      color: (theme) => theme.palette.text.primary,
                    }}
                  >
                    {moment(projectDetails?.updatedAt).format(
                      "MMM DD, YYYY hh:mm A",
                    )}
                  </Typography>
                </SettingRow>
              </Box>
            </Paper> */}
          </Stack>
        </Grid>

        {/* RIGHT COLUMN */}
        <Grid item xs={12} md={6}>
          <Stack spacing={2}>
            {/* CONFIGURATION */}
            <Paper
              elevation={0}
              sx={{
                background: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.03)"
                    : theme.palette.background.paper,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              <SectionHeader
                icon={
                  <CategoryIcon
                    sx={{
                      fontSize: 16,
                      color: (theme) => theme.palette.primary.main,
                    }}
                  />
                }
                title={t("Configuration")}
              />
              <Box>
                {/* <SettingRow
                  icon={
                    <ViewKanbanIcon
                      sx={{
                        fontSize: 16,
                        color: (theme) => theme.palette.text.secondary,
                      }}
                    />
                  }
                  label={t("Board Type")}
                >
                  {isEditingBoardType ? (
                    <ClickAwayListener
                      onClickAway={() => setIsEditingBoardType(false)}
                    >
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <FormControl size="small" sx={{ minWidth: 130 }}>
                          <Select
                            value={projectBoardType}
                            onChange={(e) =>
                              handleBoardTypeChange(e.target.value)
                            }
                            autoFocus
                            sx={formControlStyles.select}
                          >
                            <MenuItem value="KANBAN">
                              <Typography variant="body2" fontSize="12.5px">
                                {t("Kanban")}
                              </Typography>
                            </MenuItem>
                            <MenuItem value="SCRUM">
                              <Typography variant="body2" fontSize="12.5px">
                                {t("Scrum")}
                              </Typography>
                            </MenuItem>
                          </Select>
                        </FormControl>
                        {isUpdatingBoardType && <CircularProgress size={16} />}
                      </Box>
                    </ClickAwayListener>
                  ) : (
                    <>
                      <Chip
                        label={
                          projectBoardType === "KANBAN"
                            ? t("Kanban")
                            : t("Scrum")
                        }
                        size="small"
                        sx={{
                          ...formControlStyles.chip,
                          background: (theme) =>
                            `${theme.palette.primary.main}15`,
                          color: (theme) => theme.palette.primary.main,
                        }}
                      />
                       {!DEFEKT_TRACKER.HIDE_RENAME(myPermissions) && (
                        <IconButton
                          size="small"
                          onClick={() => setIsEditingBoardType(true)}
                          disabled={!DEFEKT_TRACKER.RENAME(myPermissions)}
                          sx={{
                            color: (theme) => theme.palette.text.secondary,
                          }}
                        >
                          <EditIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      )} 
                    </>
                  )}
                </SettingRow> */}

                {/* <SettingRow
                  icon={
                    <CategoryIcon
                      sx={{
                        fontSize: 16,
                        color: (theme) => theme.palette.text.secondary,
                      }}
                    />
                  }
                  label={t("Project Type")}
                >
                  <Chip
                    label={
                      projectType === "ISSUE"
                        ? t("Issue")
                        : projectType === "FEEDBACK"
                          ? t("Feedback")
                          : t("Not Set")
                    }
                    size="small"
                    sx={{
                      ...formControlStyles.chip,
                      background: (theme) =>
                        projectType
                          ? `${theme.palette.primary.main}15`
                          : `${theme.palette.text.secondary}15`,
                      color: (theme) =>
                        projectType
                          ? theme.palette.primary.main
                          : theme.palette.text.secondary,
                    }}
                  />
                </SettingRow> */}
                <SettingRow
                  icon={
                    <CategoryIcon
                      sx={{
                        fontSize: 16,
                        color: (theme) => theme.palette.text.secondary,
                      }}
                    />
                  }
                  label={t("Configuration Type")}
                >
                  <Chip
                    label={
                      projectDetails?.configurationType === "IT"
                        ? t("IT")
                        : projectDetails?.configurationType === "SUPPORT"
                          ? t("Support")
                          : t("Not Set")
                    }
                    size="small"
                    sx={{
                      ...formControlStyles.chip,
                      background: (theme) =>
                        projectDetails?.configurationType
                          ? `${theme.palette.primary.main}15`
                          : `${theme.palette.text.secondary}15`,
                      color: (theme) =>
                        projectDetails?.configurationType
                          ? theme.palette.primary.main
                          : theme.palette.text.secondary,
                    }}
                  />
                </SettingRow>

                {/* {projectType && itemTypes.length > 0 && (
                  <SettingRow
                    icon={
                      <LabelIcon
                        sx={{
                          fontSize: 16,
                          color: (theme) => theme.palette.text.secondary,
                        }}
                      />
                    }
                    label={t("Item Types")}
                  >
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {itemTypes.map((itemType, index) =>
                        editingItemTypeIndex === index ? (
                          <ClickAwayListener
                            key={itemType.label}
                            onClickAway={saveItemTypeLabel}
                          >
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <InputBase
                                value={editingItemTypeLabel}
                                onChange={(e) =>
                                  setEditingItemTypeLabel(e.target.value)
                                }
                                autoFocus
                                sx={{
                                  minWidth: 90,
                                  px: 1.5,
                                  py: 0.4,
                                  border: (theme) =>
                                    `1px solid ${theme.palette.primary.main}`,
                                  borderRadius: "6px",
                                  fontSize: "11.5px",
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") saveItemTypeLabel();
                                  if (e.key === "Escape")
                                    cancelEditingItemType();
                                }}
                              />
                              <IconButton
                                onClick={saveItemTypeLabel}
                                size="small"
                              >
                                {isUpdatingItemTypes ? (
                                  <CircularProgress size={14} />
                                ) : (
                                  <CheckIcon sx={{ fontSize: 14 }} />
                                )}
                              </IconButton>
                            </Box>
                          </ClickAwayListener>
                        ) : (
                          <Chip
                            key={itemType.label}
                            label={capitalizeLabel(itemType.label)}
                            size="small"
                            onClick={() =>
                              DEFEKT_TRACKER.RENAME(myPermissions) &&
                              startEditingItemType(index)
                            }
                            onDelete={
                              DEFEKT_TRACKER.RENAME(myPermissions) &&
                              !DEFEKT_TRACKER.HIDE_RENAME(myPermissions)
                                ? () => startEditingItemType(index)
                                : undefined
                            }
                            deleteIcon={<EditIcon sx={{ fontSize: 13 }} />}
                            sx={{
                              ...formControlStyles.chip,
                              background: (theme) =>
                                `${theme.palette.primary.main}15`,
                              color: (theme) => theme.palette.primary.main,
                              cursor: DEFEKT_TRACKER.RENAME(myPermissions)
                                ? "pointer"
                                : "default",
                            }}
                          />
                        ),
                      )}
                    </Box>
                  </SettingRow>
                )} */}

                {/* <SettingRow
                  icon={
                    <WorkspacesIcon
                      sx={{
                        fontSize: 16,
                        color: (theme) => theme.palette.text.secondary,
                      }}
                    />
                  }
                  label={t("Skill Set")}
                  noBorder
                >
                  {isUpdatingSkillSet ? (
                    <CircularProgress size={18} />
                  ) : (
                    <Autocomplete
                      multiple
                      options={skillSetOptions}
                      value={skillSet || []}
                      onChange={(event, newValue) => {
                        handleSaveSkillSet(newValue);
                      }}
                      disabled={!DEFEKT_TRACKER.RENAME(myPermissions)}
                      size="small"
                      renderTags={(tagValues, getTagProps) =>
                        tagValues.map((option, index) => (
                          <Chip
                            key={index}
                            label={option}
                            {...getTagProps({ index })}
                            size="small"
                            sx={{
                              ...formControlStyles.chip,
                              background: (theme) =>
                                `${theme.palette.primary.main}15`,
                              color: (theme) => theme.palette.primary.main,
                              border: "none",
                            }}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={
                            skillSet.length === 0 ? t("Select skills") : ""
                          }
                          size="small"
                        />
                      )}
                      sx={{
                        minWidth: 200,
                        ...formControlStyles.autocomplete,
                      }}
                    />
                  )}
                </SettingRow> */}

                {/* Project Preferences */}
                <SettingRow
                  icon={
                    <ViewColumnIcon
                      sx={{
                        fontSize: 16,
                        color: (theme) => theme.palette.text.secondary,
                      }}
                    />
                  }
                  label={t("Project Preference")}
                  subtitle={
                    projectDetails?.configurationType !== "IT"
                      ? t("Toggle sidebar sections visibility")
                      : undefined
                  }
                  noBorder
                >
                  {isEditMode && projectDetails?.configurationType !== "IT" ? (
                    <Autocomplete
                      multiple
                      options={["Feedback", "Task Board", "Live Logs"]}
                      value={
                        [
                          preference.isFeedback && "Feedback",
                          preference.isBoard && "Task Board",
                          preference.isLiveLog && "Live Logs",
                        ].filter(Boolean) as string[]
                      }
                      onChange={(event, newValue) => {
                        if (newValue.length === 0) return;
                        setPreference({
                          isFeedback: newValue.includes("Feedback"),
                          isBoard: newValue.includes("Task Board"),
                          isLiveLog: newValue.includes("Live Logs"),
                        });
                      }}
                      size="small"
                      disableClearable
                      getOptionLabel={(option) => t(option)}
                      renderTags={(tagValues, getTagProps) =>
                        tagValues.map((option, index) => (
                          <Chip
                            key={index}
                            label={t(option)}
                            {...getTagProps({ index })}
                            size="small"
                            sx={{
                              ...formControlStyles.chip,
                              background: (theme) =>
                                `${theme.palette.primary.main}15`,
                              color: (theme) => theme.palette.primary.main,
                              border: "none",
                            }}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={
                            !preference.isFeedback &&
                            !preference.isBoard &&
                            !preference.isLiveLog
                              ? t("Select sections")
                              : ""
                          }
                          size="small"
                        />
                      )}
                      sx={{
                        minWidth: 200,
                        ...formControlStyles.autocomplete,
                      }}
                    />
                  ) : (
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {preference.isFeedback && (
                        <Chip
                          label={t("Feedback")}
                          size="small"
                          sx={{
                            ...formControlStyles.chip,
                            background: (theme) =>
                              `${theme.palette.primary.main}15`,
                            color: (theme) => theme.palette.primary.main,
                            border: "none",
                          }}
                        />
                      )}
                      {preference.isBoard && (
                        <Chip
                          label={t("Task Board")}
                          size="small"
                          sx={{
                            ...formControlStyles.chip,
                            background: (theme) =>
                              `${theme.palette.primary.main}15`,
                            color: (theme) => theme.palette.primary.main,
                            border: "none",
                          }}
                        />
                      )}
                      {preference.isLiveLog && (
                        <Chip
                          label={t("Live Logs")}
                          size="small"
                          sx={{
                            ...formControlStyles.chip,
                            background: (theme) =>
                              `${theme.palette.primary.main}15`,
                            color: (theme) => theme.palette.primary.main,
                            border: "none",
                          }}
                        />
                      )}
                    </Box>
                  )}
                </SettingRow>
              </Box>
            </Paper>

            {/* WORKFLOW SETTINGS - only visible when Task Board is selected */}
            {preference?.isBoard && (
              <Paper
                elevation={0}
                sx={{
                  background: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.03)"
                      : theme.palette.background.paper,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                <SectionHeader
                  icon={
                    <FlagIcon
                      sx={{
                        fontSize: 16,
                        color: (theme) => theme.palette.primary.main,
                      }}
                    />
                  }
                  title={t("Workflow")}
                />
                <Box>
                  <SettingRow
                    icon={
                      <CheckIcon
                        sx={{
                          fontSize: 16,
                          color: (theme) => theme.palette.text.secondary,
                        }}
                      />
                    }
                    label={t("Done Status")}
                  >
                    {isStatusLoading ? (
                      <CircularProgress size={18} />
                    ) : isEditMode ? (
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <Select
                          value={doneStatusId || ""}
                          onChange={(e) => setDoneStatusId(e.target.value)}
                          displayEmpty
                          sx={formControlStyles.select}
                        >
                          <MenuItem value="">
                            <Typography variant="body2" fontSize="12.5px">
                              {t("Not Selected")}
                            </Typography>
                          </MenuItem>
                          {availableStatus.map((status) => (
                            <MenuItem key={status?._id} value={status?._id}>
                              <Typography variant="body2" fontSize="12.5px">
                                {status?.status ||
                                  status?.label ||
                                  status?.name}
                              </Typography>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "12.5px",
                          fontWeight: 500,
                          color: (theme) => theme.palette.text.primary,
                        }}
                      >
                        {doneStatusName}
                      </Typography>
                    )}
                  </SettingRow>

                  {!isSniffer && <SettingRow
                    icon={
                      <TimerIcon
                        sx={{
                          fontSize: 16,
                          color: (theme) => theme.palette.text.secondary,
                        }}
                      />
                    }
                    label={t("Estimation")}
                    subtitle={t("Require time estimation for tasks")}
                  >
                    {isEditMode ? (
                      <IOSSwitch
                        checked={isEstimationRequired}
                        onChange={(e) =>
                          setIsEstimationRequired(e.target.checked)
                        }
                      />
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "12.5px",
                          fontWeight: 500,
                          color: (theme) =>
                            isEstimationRequired
                              ? theme.palette.success.main
                              : theme.palette.text.secondary,
                        }}
                      >
                        {isEstimationRequired ? t("Yes") : t("No")}
                      </Typography>
                    )}
                  </SettingRow>}

                  <SettingRow
                    icon={
                      <LinkIcon
                        sx={{
                          fontSize: 16,
                          color: (theme) => theme.palette.text.secondary,
                        }}
                      />
                    }
                    label={t("Story Link")}
                    subtitle={t("Link stories to specific item types")}
                    noBorder
                  >
                    {isEditMode ? (
                      <Autocomplete
                        multiple
                        options={["DEFECT", "SUGGESTIONS"]}
                        value={storyRequiredTypes || []}
                        onChange={(event, newValue) => {
                          setStoryRequiredTypes(newValue);
                        }}
                        size="small"
                        renderTags={(tagValues, getTagProps) =>
                          tagValues.map((option, index) => (
                            <Chip
                              key={index}
                              label={t(option)}
                              {...getTagProps({ index })}
                              size="small"
                              sx={{
                                ...formControlStyles.chip,
                                background: (theme) =>
                                  `${theme.palette.primary.main}15`,
                                color: (theme) => theme.palette.primary.main,
                                border: "none",
                              }}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder={
                              storyRequiredTypes.length === 0
                                ? t("Select types")
                                : ""
                            }
                            size="small"
                          />
                        )}
                        getOptionLabel={(option) => t(option)}
                        sx={{
                          minWidth: 200,
                          ...formControlStyles.autocomplete,
                        }}
                      />
                    ) : (
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {(storyRequiredTypes || []).map((type) => (
                          <Chip
                            key={type}
                            label={t(type)}
                            size="small"
                            sx={{
                              ...formControlStyles.chip,
                              background: (theme) =>
                                `${theme.palette.primary.main}15`,
                              color: (theme) => theme.palette.primary.main,
                              border: "none",
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </SettingRow>
                </Box>
              </Paper>
            )}

            {/* AUTOMATION */}
            {/* {preference?.isLiveLog !== false && (
              <Paper
                elevation={0}
                sx={{
                  background: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.03)"
                      : theme.palette.background.paper,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                <SectionHeader
                  icon={
                    <BugReportIcon
                      sx={{
                        fontSize: 16,
                        color: (theme) => theme.palette.primary.main,
                      }}
                    />
                  }
                  title={t("Automation")}
                />
                <Box>
                  <SettingRow
                    icon={
                      <BugReportIcon
                        sx={{
                          fontSize: 16,
                          color: (theme) => theme.palette.text.secondary,
                        }}
                      />
                    }
                    label={t("Auto Task")}
                    subtitle={t("Automatically create tasks from Live Logs")}
                    noBorder={!isAutoTaskFromSystemBug}
                  >
                    {isEditMode ? (
                      <IOSSwitch
                        checked={isAutoTaskFromSystemBug}
                        onChange={(e) =>
                          setIsAutoTaskFromSystemBug(e.target.checked)
                        }
                      />
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "12.5px",
                          fontWeight: 500,
                          color: (theme) =>
                            isAutoTaskFromSystemBug
                              ? theme.palette.success.main
                              : theme.palette.text.secondary,
                        }}
                      >
                        {isAutoTaskFromSystemBug ? t("Yes") : t("No")}
                      </Typography>
                    )}
                  </SettingRow>

                  {isAutoTaskFromSystemBug && (
                    <Box
                      sx={{
                        px: 2,
                        py: 1.5,
                        background: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.02)"
                            : "rgba(0, 0, 0, 0.015)",
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={2}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "12px",
                            color: (theme) => theme.palette.text.secondary,
                            fontWeight: 500,
                          }}
                        >
                          {t("Default Status")}
                        </Typography>
                        {isStatusLoading ? (
                          <CircularProgress size={16} />
                        ) : isEditMode ? (
                          <FormControl size="small" sx={{ minWidth: 140 }}>
                            <Select
                              value={systemAutoBugStatusId || ""}
                              onChange={(e) =>
                                setSystemAutoBugStatusId(e.target.value)
                              }
                              displayEmpty
                              sx={{
                                ...formControlStyles.select,
                                fontSize: "12px",
                                height: "32px",
                              }}
                            >
                              <MenuItem value="">
                                <Typography variant="body2" fontSize="12px">
                                  {t("Select")}
                                </Typography>
                              </MenuItem>
                              {availableStatus.map((status) => (
                                <MenuItem key={status?._id} value={status?._id}>
                                  <Typography variant="body2" fontSize="12px">
                                    {status?.status ||
                                      status?.label ||
                                      status?.name}
                                  </Typography>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "12px",
                              fontWeight: 500,
                              color: (theme) => theme.palette.text.primary,
                            }}
                          >
                            {autoBugStatusName}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Paper>
            )} */}

            {/* DANGER ZONE */}
            {!DEFEKT_TRACKER.HIDE_DELETE(myPermissions) &&
              DEFEKT_TRACKER.DELETE(myPermissions) &&
              currentUser?._id === projectDetails?.owner && (
                <Accordion
                  disableGutters
                  elevation={0}
                  sx={{
                    background: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255, 0, 0, 0.05)"
                        : "rgba(255, 0, 0, 0.02)",
                    border: (theme) =>
                      `1px solid ${
                        theme.palette.mode === "dark"
                          ? "rgba(255, 0, 0, 0.2)"
                          : "rgba(255, 0, 0, 0.15)"
                      }`,
                    borderRadius: "8px !important",
                    overflow: "hidden",
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon
                        sx={{
                          fontSize: 18,
                          color: (theme) =>
                            theme.palette.mode === "dark"
                              ? "rgba(255, 100, 100, 0.8)"
                              : "rgba(200, 0, 0, 0.6)",
                        }}
                      />
                    }
                    sx={{
                      minHeight: "42px",
                      px: 2,
                      "& .MuiAccordionSummary-content": {
                        my: 1,
                      },
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1.2}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "6px",
                          background: (theme) =>
                            theme.palette.mode === "dark"
                              ? "rgba(255, 100, 100, 0.15)"
                              : "rgba(255, 0, 0, 0.08)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <WarningAmberIcon
                          sx={{
                            fontSize: 16,
                            color: (theme) =>
                              theme.palette.mode === "dark"
                                ? "rgba(255, 100, 100, 0.8)"
                                : "rgba(200, 0, 0, 0.7)",
                          }}
                        />
                      </Box>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        sx={{
                          fontSize: "13px",
                          color: (theme) =>
                            theme.palette.mode === "dark"
                              ? "rgba(255, 100, 100, 0.8)"
                              : "rgba(200, 0, 0, 0.7)",
                        }}
                      >
                        {t("Danger Zone")}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    <AdvanceTab />
                  </AccordionDetails>
                </Accordion>
              )}
          </Stack>
        </Grid>
      </Grid>
    </Container>
  );
};

export default SettingsTab;
